import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} MemberUpdateData
 * @property {string} [firstName] - First Name of member
 * @property {string} [lastName] - Last Name of member
 * @property {string} [email] - Email of member
 * @property {string} [role_slug] - Role slug of member
 */

/**
 * Custom hook to update member details
 */
export const useUpdateMemberDetail = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { axiosInstance } = useAxios();

  /**
   * Update Member by ID
   * @param {object} params
   * @param {number|string} params.memberId - The ID of the member to update
   * @param {MemberUpdateData} params.updatedData - The data to update
   * @param {() => void} [params.onSuccess] - Optional callback on successful update
   * @returns {Promise<object|null>} Returns the response data or null on error
   */
  const updateMemberDetail = async ({ memberId, updatedData, onSuccess }) => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.put(
        `${TENANT_DOMAIN}/school/admin/member/update/${memberId}`,
        {
          firstName: updatedData.firstName,
          lastName: updatedData.lastName,
          email: updatedData.email,
          role_slug: updatedData.role_slug,
        },
      );

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }

      return response.data;
    } catch (err) {
      if (err?.response?.status === 500 || err?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        const apiErrors = err?.response?.data?.errors;
        const firstError = apiErrors && Object.values(apiErrors)?.[0]?.[0];

        setError(
          firstError || err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN,
        );
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { updateMemberDetail, loading, error };
};
